import React from "react";
import {observer} from "mobx-react";
import {Box, Modal} from "@mui/material";
import styled from "@emotion/styled";
import {useNavigate} from "react-router-dom";
import {useLocation} from "react-router";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {modalSponsorLogo, sponsorLink} from "data/constants/header";
import {SYNDICATE} from "data/constants";
import {SyndicatesType} from "data/enums";
import {IModalWelcomeLightPredictorController} from "./modal_welcome.controller";
import {PrimaryButtonInvert, PureButton, SponsorWrapper} from "views/components/style";
import {media} from "assets/css/media";
import {ReactComponent as LogoIcon} from "assets/img/icons/modal/welcomeModalLogo.svg";
import {LanguageSelect} from "views/components/header/language_select/language_select.component";

const ModalContent = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	outline: none;
	max-width: 600px;
	width: 100%;
	background: #fff;
	padding: 20px 16px 16px;
	border-radius: 4px;
	text-align: center;
	color: #151515;

	@media screen and (max-width: 640px) {
		padding-top: 16px;
		max-width: unset;
		left: 24px;
		right: 24px;
		width: auto;
		transform: translateY(-50%);
	}

	button {
		max-width: 300px;
	}
`;

const Icon = styled.div`
	margin-bottom: 12px;

	svg {
		width: 281px;
	}

	@media (min-width: ${media.mobile}) {
		margin-bottom: 16px;
	}
`;

const Title = styled.h1`
	font-size: 20px;
	font-weight: 600;
	line-height: 130%;
	margin-bottom: 8px;
	color: #103365;

	@media (min-width: ${media.mobile}) {
		font-size: 24px;
		margin-bottom: 12px;
	}
`;

const Desc = styled.p`
	font-size: 16px;
	font-weight: 400;
	margin-bottom: 16px;
`;

const CloseButton = styled(PureButton)`
	color: #151515;
	text-align: center;
	font-weight: 600;
	line-height: 140%;
	text-decoration-line: underline;
	margin-top: 12px;
	font-size: 14px;

	@media (min-width: ${media.mobile}) {
		font-size: 16px;
		margin-top: 16px;
	}
`;

const SponsorIcon = styled.img`
	height: 32px;
	width: auto;
	margin-bottom: 16px;
	object-fit: contain;

	@media (min-width: ${media.tablet}) {
		height: 40px;
	}

	&.${SyndicatesType.CA}, &.${SyndicatesType.DE} {
		height: 24px;
	}

	&.${SyndicatesType.US} {
		height: 50px;

		@media (min-width: ${media.mobile}) {
			height: 60px;
		}
	}
`;
const LanguageSelectBox = styled(Box)`
	position: absolute;
	top: 20px;
	right: 20px;

	color: #000000;
`;

export const ModalWelcome: React.FC = observer(() => {
	const {isOpen, i18n, closeHandler, redirectHandler} =
		useViewController<IModalWelcomeLightPredictorController>(
			Bindings.ModalWelcomeLightPredictorController,
			{
				navigate: useNavigate(),
				location: useLocation(),
			}
		);

	return (
		<Modal open={isOpen}>
			<ModalContent>
				{SYNDICATE.toLowerCase() === SyndicatesType.US && (
					<LanguageSelectBox>
						<LanguageSelect />
					</LanguageSelectBox>
				)}

				<Icon>
					<LogoIcon />
				</Icon>

				{modalSponsorLogo && (
					<SponsorWrapper link={sponsorLink}>
						<SponsorIcon className={SYNDICATE} src={modalSponsorLogo} />
					</SponsorWrapper>
				)}

				<Title>{i18n.t("modal.welcome_lp.title")}</Title>

				<Desc>{i18n.t("modal.welcome_lp.desc")}</Desc>

				<PrimaryButtonInvert onClick={closeHandler}>
					{i18n.t("modal.welcome_lp.button")}
				</PrimaryButtonInvert>
				<CloseButton onClick={redirectHandler}>
					{i18n.t("modal.welcome_lp.close")}
				</CloseButton>
			</ModalContent>
		</Modal>
	);
});
